import { useState, useEffect, useCallback } from "react";
import { timeEntryAPI } from "../utils/api";
import { features } from "../config/features";

/**
 * Hook to fetch and manage active timers for a specific project
 * Handles real-time updates via socket events
 */
export const useProjectActiveTimers = (projectId, socket) => {
  const [activeTimers, setActiveTimers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all active timers for the project
  const fetchActiveTimers = useCallback(async () => {
    if (!projectId || !features.timeTracking) return;

    setLoading(true);
    setError(null);

    try {
      const response = await timeEntryAPI.getProjectActiveTimers(projectId);

      if (response.data.success) {
        // Create a map of cardId -> activeTimer for easy access
        const timerMap = {};
        response.data.activeTimers.forEach((timer) => {
          timerMap[timer.cardId] = timer;
        });
        setActiveTimers(timerMap);
      }
    } catch (err) {
      console.error("Failed to fetch active timers:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Initial fetch on mount or when projectId changes
  useEffect(() => {
    fetchActiveTimers();
  }, [fetchActiveTimers]);

  // Listen for real-time timer events via socket
  useEffect(() => {
    if (!socket || !projectId || !features.timeTracking) return;

    // When a timer starts on a card in this project
    const handleTimerStarted = (data) => {
      if (data.cardId) {
        // Fetch the updated timers to get user details
        fetchActiveTimers();
      }
    };

    // When a timer stops on a card in this project
    const handleTimerStopped = (data) => {
      if (data.cardId) {
        setActiveTimers((prev) => {
          const updated = { ...prev };
          delete updated[data.cardId];
          return updated;
        });
      }
    };

    // When a timer is discarded
    const handleTimerDiscarded = (data) => {
      if (data.cardId) {
        setActiveTimers((prev) => {
          const updated = { ...prev };
          delete updated[data.cardId];
          return updated;
        });
      }
    };

    // Listen to project-specific socket events
    socket.on("timer-started", handleTimerStarted);
    socket.on("timer-stopped", handleTimerStopped);
    socket.on("timer-discarded", handleTimerDiscarded);

    return () => {
      socket.off("timer-started", handleTimerStarted);
      socket.off("timer-stopped", handleTimerStopped);
      socket.off("timer-discarded", handleTimerDiscarded);
    };
  }, [socket, projectId, fetchActiveTimers]);

  return {
    activeTimers,
    loading,
    error,
    refetch: fetchActiveTimers,
  };
};
